"use client";

import Image from "next/image";
import { Mail } from "lucide-react";
import type { TeamMember } from "@/content/team";
import { site } from "@/content/site";

interface LeadershipCardProps {
  member: TeamMember;
}

export function LeadershipCard({ member }: LeadershipCardProps) {
  const mailto = `mailto:${site.contact.email}?subject=Hello%20${encodeURIComponent(member.name)}`;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--site-border)] bg-[var(--site-surface)] transition-shadow duration-300 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)]">
      <div className="relative aspect-square overflow-hidden bg-[var(--site-surface-alt)]">
        <Image
          src={member.image}
          alt={member.name}
          fill
          className="object-cover object-top transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:scale-[1.03]"
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
        />
      </div>

      <div className="flex flex-1 flex-col px-5 pb-5 pt-4 md:px-5 md:pb-5">
        <div className="mb-3 h-px w-8 bg-[var(--site-accent)] transition-all duration-300 group-hover:w-12" />
        <h3 className="font-display text-lg font-bold tracking-tight text-[var(--site-text)] md:text-xl">
          {member.name}
        </h3>
        <p className="mt-2 inline-flex w-fit max-w-full rounded-full border border-[var(--site-accent)]/35 bg-[var(--site-accent)]/10 px-2.5 py-1 text-left text-[11px] font-semibold leading-snug text-[var(--site-accent)] md:text-xs">
          {member.role}
        </p>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-[var(--site-muted)]">
          {member.bio}
        </p>
        <a
          href={mailto}
          className="mt-4 inline-flex w-fit items-center gap-1.5 text-sm font-medium text-[var(--site-muted)] transition-colors hover:text-[var(--site-primary)]"
        >
          <Mail size={14} aria-hidden className="opacity-70" />
          Contact
        </a>
      </div>
    </article>
  );
}
